/** Barra superior da área logada (DESIGN.md § 3.2).
 *
 * Existe para separar navegação de conteúdo. Antes, as ações de conta ficavam
 * na mesma linha do título "Oportunidades" — e o cabeçalho da tela tinha de
 * carregar duas funções ao mesmo tempo: dizer onde a pessoa está e oferecer o
 * que fazer fora dali. Empurrar a navegação para uma faixa própria libera o
 * cabeçalho para ser só cabeçalho.
 *
 * Fundo de superfície sobre canvas: a faixa se separa por contraste de plano,
 * não por peso de cor. Uma barra escura no topo seria o tipo de faixa
 * decorativa que o § 1.2 evita.
 */

import { inicial, iniciais, primeiroNome } from "./iniciais";

import type { ReactNode } from "react";

interface Props {
  /** Nome do tenant. A inicial vira a marca à esquerda. */
  empresa: string;
  /** Linha de contexto abaixo do nome — onde a pessoa está no produto. */
  contexto: string;
  usuario: string;
  acoes?: ReactNode;
  /** Abre a conta da pessoa. Quando ausente, o bloco do nome é só identificação.
   *
   * O nome, e não mais um botão: a barra já tem dez ações, e "clicar no próprio
   * nome" é onde se procura a própria conta em qualquer produto da web. */
  aoAbrirConta?: () => void;
  /** Marca o bloco do nome com um ponto âmbar — hoje, segundo fator desligado.
   * Ele vivia no botão "Segurança"; com a tela de conta reunindo o que é da
   * pessoa, o sinal acompanha o lugar para onde ele leva. */
  alerta?: boolean;
}

/** O que o ponto âmbar quer dizer — em palavras, num lugar só.
 *
 * Usado pelo texto de leitor de tela e pelo `title` de quem passa o mouse. A
 * tela Pessoas já escreve o mesmo fato por extenso ("Segundo fator desligado");
 * duas linguagens para a mesma coisa custam uma tradução a quem lê. */
export const AVISO_DE_SEGUNDO_FATOR = "Segundo fator desligado — ative em Minha conta";

export function Topbar({ empresa, contexto, usuario, acoes, aoAbrirConta, alerta }: Props) {
  const identidade = (
    <>
      <span
        aria-hidden="true"
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-acao/10 font-texto text-micro font-bold text-acao"
      >
        {iniciais(usuario)}
      </span>
      {/* Cor não é o único sinal (DESIGN.md § 8).
          O ponto era `aria-hidden` e sem texto: para quem enxerga, um ponto
          colorido ao lado do nome lê-se como "online", que é o que ele
          significa em quase toda a web; para leitor de tela, não existia. Um
          alerta que ninguém decodifica não leva ninguém a ligar o segundo
          fator — e gasta o âmbar, que o § 1.3 reserva para o que exige ação. */}
      {alerta === true && (
        <>
          <span aria-hidden="true" className="-ml-1 text-ambar">
            ●
          </span>
          <span className="sr-only">{AVISO_DE_SEGUNDO_FATOR}</span>
        </>
      )}
      <span className="font-texto text-caption text-texto">{primeiroNome(usuario)}</span>
    </>
  );

  return (
    // `sticky top-0`, e não `fixed`: a barra acompanha a rolagem sem sair do
    // fluxo, então nenhuma tela precisa reservar espaço embaixo dela — um
    // `fixed` cobriria o primeiro título de cada página até alguém lembrar de
    // compensar com `padding`, tela por tela.
    //
    // O `z-20` fica **abaixo** do que as folhas usam para escurecer o fundo e
    // acima de todo conteúdo comum: a barra é teto do produto, não da caixa de
    // diálogo que a própria pessoa abriu.
    <header
      aria-label="Barra do produto"
      className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-4 border-b border-borda bg-superficie px-6 py-3"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden="true"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-acao font-display text-caption font-bold text-acao-contraste"
        >
          {inicial(empresa)}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-texto text-caption font-semibold text-texto">
            {empresa}
          </span>
          <span className="truncate font-texto text-micro uppercase tracking-wide text-texto-suave">
            {contexto}
          </span>
        </span>
      </div>

      {/* `flex-wrap` aqui, e não só no `header`: são nove ações para quem
          administra a conta, e numa linha só elas somam mais de 900 px. O
          `header` quebrava entre a identidade da empresa e o bloco de ações, e
          o bloco continuava indivisível — o que empurrava a **página inteira**
          para 968 px de largura num aparelho de 390 px. O efeito não era a
          barra cortada: era todo o produto rolando de lado no celular, com o
          funil e a ficha nascendo fora da tela. */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* As ações somem abaixo de 640px: a navegação inferior as carrega, e
            manter as duas duplicaria a barra em três linhas exatamente onde a
            tela é mais curta. O nome, esse fica — é identidade, não navegação. */}
        <span className="hidden flex-wrap items-center gap-2 sm:flex">{acoes}</span>
        {acoes !== undefined && (
          <span aria-hidden="true" className="mx-1 hidden h-6 w-px shrink-0 bg-borda sm:block" />
        )}
        {aoAbrirConta === undefined ? (
          <span className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3">{identidade}</span>
        ) : (
          <button
            type="button"
            onClick={aoAbrirConta}
            aria-label="Minha conta"
            title={alerta === true ? AVISO_DE_SEGUNDO_FATOR : undefined}
            className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
          >
            {identidade}
          </button>
        )}
      </div>
    </header>
  );
}
